// TEAM surface (N-FDE-1 AC-2), the "Signal" re-layout (Team v2) — the people rail beside the person
// detail card, from the claude.ai/design 'Team Screen (Signal)' mockup, over the SAME projection and
// routes as before. The dashboard renders the `identity` (A) directory (no credential value, no raw
// address — G-140/G-058). A directory CHANGE is a routed PROPOSAL (BUILD-DEC-115: people propose — any
// active human; the agent can too): "Suggest a change" on a person routes `edit_user`, "Add someone"
// routes `add_user` — both carrying the human's MANDATORY description (N8).
//
// BUILD-DEC-127 (Paul's 2026-08-01 ruling — "the propose change thing is complete BS"): the
// PENDING-CHANGES QUEUE IS GONE. It was ceremony: a key-holder routed requests to themselves, and
// accepting one applied nothing anyway (resolveProposal records the decision only). A key-holder now
// EDITS CONTACT DETAILS DIRECTLY (routeContactAction → the identity write legs, credential-enforced);
// "Suggest a change" survives ONLY for someone WITHOUT the key. The agent guard is untouched by
// explicit ruling: it may propose, it never commits (G-001). Tokens only (G-181).

import { useCallback, useState } from 'react';
import { useSurfaceApi } from './SurfaceApiContext.tsx';
import { useProjection } from './useProjection.ts';
import { SurfaceFrame } from './surface-frame.tsx';
import { TeamPeopleRail } from './components/TeamPeopleRail.tsx';
import { TeamPersonDetail } from './components/TeamPersonDetail.tsx';
import { TeamAddPane } from './components/TeamAddPane.tsx';
import type { NewDirectoryPerson } from './components/TeamAddPane.tsx';
import type { EditablePersonPatch } from './components/team-directory.ts';
import { ScreenToolbar, ScreenBody, TbStamp, TbInfo } from '../app/screen-chrome.tsx';
import type { DirectoryProjection, UserRow } from './know-projections.ts';
import type { RouteResult } from './api-client.ts';

/** Which view the right pane shows — the person detail or the add pane (the queue is gone). */
type TeamPane = 'person' | 'add';

/** The routed-outcome note — honest routed/refused copy with its tone (the F6/N7 surfacing rules). */
interface ProposeNote {
  readonly text: string;
  readonly tone: 'ok' | 'warn';
}

/** The routed directory intents + the outcome note they own (the mutation seam, all proposals). */
interface DirectoryRoutes {
  readonly proposeNote: ProposeNote | null;
  readonly clearProposeNote: () => void;
  readonly onProposeChange: (userId: string, summary: string) => Promise<RouteResult>;
  readonly onProposeAdd: (summary: string) => Promise<RouteResult>;
  /** DIRECT commits (BUILD-DEC-127) — the owning identity legs change the directory for real. */
  readonly onAddAddress: (userId: string, address: string) => void;
  readonly onRetireAddress: (channelIdentityId: string) => void;
  readonly onCreatePerson: (person: NewDirectoryPerson) => Promise<RouteResult>;
  readonly onEditPerson: (userId: string, patch: EditablePersonPatch) => void;
}

/** Pane + person selection (client view-state). */
interface TeamSelection {
  readonly pane: TeamPane;
  readonly selectedId: string | null;
  readonly onSelectPerson: (userId: string) => void;
  readonly onToggleAdd: () => void;
  readonly onClosePane: () => void;
}

/** The refusal note carries the transport's sanitised message, never a GUESSED cause (N7). */
function refusalNote(result: RouteResult): ProposeNote {
  return {
    tone: 'warn',
    text:
      result.note !== undefined && result.note.length > 0
        ? `That did not go through — ${result.note}. Nothing was changed.`
        : 'That did not go through. Nothing was changed.',
  };
}

/** Fold one routed outcome into the note + the ok-path re-read (G-182); returns the result as-is. */
function routedOutcome(
  result: RouteResult,
  okText: string,
  setNote: (note: ProposeNote | null) => void,
  reload: () => void,
): RouteResult {
  if (result.ok) {
    setNote({ tone: 'ok', text: okText });
    reload();
  } else {
    setNote(refusalNote(result));
  }
  return result;
}

/** The two routed directory PROPOSE intents (edit + add) + the resolve leg + the outcome note, which
 *  lives HERE — above the `SurfaceFrame` remount boundary: the re-read a routed ok triggers (G-182)
 *  remounts the body, and a note held inside it dies before it can render (the N5 remount lesson). */
function useDirectoryRoutes(reload: () => void): DirectoryRoutes {
  const api = useSurfaceApi();
  const [proposeNote, setProposeNote] = useState<ProposeNote | null>(null);
  // People propose (BUILD-DEC-115): an edit_user proposal about the selected person — or an add_user
  // proposal with NO target (the person does not exist yet) — carrying the human's own change
  // description (the text a reviewer would read).
  const onProposeChange = useCallback(
    async (userId: string, summary: string) =>
      routedOutcome(
        await api.routeDirectoryPropose('edit_user', summary, userId),
        'Proposed — the change routes to the Operator to resolve.',
        setProposeNote,
        reload,
      ),
    [api, reload],
  );
  const onProposeAdd = useCallback(
    async (summary: string) =>
      routedOutcome(
        await api.routeDirectoryPropose('add_user', summary),
        'Proposed — adding someone routes to the Operator to resolve.',
        setProposeNote,
        reload,
      ),
    [api, reload],
  );
  const { onAddAddress, onRetireAddress, onCreatePerson, onEditPerson } = useContactCommits(
    setProposeNote,
    reload,
  );
  const clearProposeNote = useCallback(() => setProposeNote(null), []);
  return {
    proposeNote,
    clearProposeNote,
    onProposeChange,
    onProposeAdd,
    onAddAddress,
    onRetireAddress,
    onCreatePerson,
    onEditPerson,
  };
}

/** The DIRECT contact commits (BUILD-DEC-127) — `identity` gates and commits for real; the surface
 *  re-reads on ok (G-182) and surfaces a refusal's sanitised note verbatim, never a guess (N7). */
function useContactCommits(
  setNote: (note: ProposeNote | null) => void,
  reload: () => void,
): {
  readonly onAddAddress: (userId: string, address: string) => void;
  readonly onRetireAddress: (channelIdentityId: string) => void;
  readonly onCreatePerson: (person: NewDirectoryPerson) => Promise<RouteResult>;
  readonly onEditPerson: (userId: string, patch: EditablePersonPatch) => void;
} {
  const api = useSurfaceApi();
  const onAddAddress = useCallback(
    (userId: string, address: string) => {
      void api.routeContactAction({ action: 'add', userId, address }).then((result) =>
        routedOutcome(
          result,
          // Honest since BUILD-DEC-159: the address is CONFIRMED the moment a directory
          // administrator saves it, so Ritona can write to it and recognise replies from it right
          // away. The old line promised the person would "confirm it from that inbox" — a step
          // that never existed, while the address sat unusable in both directions.
          `${address} added — Ritona can reach them there now.`,
          setNote,
          reload,
        ),
      );
    },
    [api, setNote, reload],
  );
  const onRetireAddress = useCallback(
    (channelIdentityId: string) => {
      void api
        .routeContactAction({ action: 'retire', channelIdentityId })
        .then((result) =>
          routedOutcome(
            result,
            'Address retired — Ritona no longer trusts mail from it.',
            setNote,
            reload,
          ),
        );
    },
    [api, setNote, reload],
  );
  return {
    onAddAddress,
    onRetireAddress,
    onCreatePerson: useCreatePerson(setNote, reload),
    onEditPerson: usePersonEdit(setNote, reload),
  };
}

/** One-save person creation; the identity domain owns the atomic User + addresses transition. */
function useCreatePerson(
  setNote: (note: ProposeNote | null) => void,
  reload: () => void,
): (person: NewDirectoryPerson) => Promise<RouteResult> {
  const api = useSurfaceApi();
  return useCallback(
    async (person: NewDirectoryPerson) =>
      routedOutcome(
        await api.routeContactAction({ action: 'createPerson', ...person }),
        `${person.displayName} added — their details and email addresses are saved.`,
        setNote,
        reload,
      ),
    [api, setNote, reload],
  );
}

/** The name/responsibilities/access commit. Identity resets sign-off defaults when access changes, so
 *  the receipt says so rather than letting it surprise anyone. */
function usePersonEdit(
  setNote: (note: ProposeNote | null) => void,
  reload: () => void,
): (userId: string, patch: EditablePersonPatch) => void {
  const api = useSurfaceApi();
  return useCallback(
    (userId: string, patch: EditablePersonPatch) => {
      const okText =
        patch.role !== undefined
          ? 'Access saved — their sign-off list is back to that level’s defaults.'
          : patch.responsibilities !== undefined
            ? 'What they do is saved — Ritona can read it.'
            : 'Name saved.';
      void api
        .routeContactAction({ action: 'editPerson', userId, ...patch })
        .then((result) => routedOutcome(result, okText, setNote, reload));
    },
    [api, setNote, reload],
  );
}

/** Pane + person selection, clearing the outcome note on every focus change (a stale note must never
 *  describe a DIFFERENT person or pane). */
function useTeamSelection(clearProposeNote: () => void): TeamSelection {
  const [pane, setPane] = useState<TeamPane>('person');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const onSelectPerson = useCallback(
    (userId: string) => {
      clearProposeNote();
      setSelectedId(userId);
      setPane('person');
    },
    [clearProposeNote],
  );
  const onToggleAdd = useCallback(() => {
    clearProposeNote();
    setPane((current) => (current === 'add' ? 'person' : 'add'));
  }, [clearProposeNote]);
  const onClosePane = useCallback(() => setPane('person'), []);
  return { pane, selectedId, onSelectPerson, onToggleAdd, onClosePane };
}

/** The head row above the columns — just Add someone (the resolve queue is gone, BUILD-DEC-127). */
function TeamHead({
  pane,
  sel,
}: {
  readonly pane: TeamPane;
  readonly sel: TeamSelection;
}): React.JSX.Element {
  return (
    <div className="tm-head">
      <span className="spacer" aria-hidden="true" />
      <button
        type="button"
        className="btn primary sm"
        aria-pressed={pane === 'add'}
        onClick={sel.onToggleAdd}
      >
        Add someone
      </button>
    </div>
  );
}

/** The right pane — the add pane, or the selected person's detail card. */
function TeamRightPane({
  directory,
  effectivePane,
  selected,
  sel,
  routes,
}: {
  readonly directory: DirectoryProjection;
  readonly effectivePane: TeamPane;
  readonly selected: UserRow | null;
  readonly sel: TeamSelection;
  readonly routes: DirectoryRoutes;
}): React.JSX.Element {
  if (effectivePane === 'add') {
    return (
      <TeamAddPane
        canCreateDirectly={directory.canEditContacts}
        onCreate={routes.onCreatePerson}
        onSuggest={(summary) => void routes.onProposeAdd(summary)}
        onCancel={sel.onClosePane}
      />
    );
  }
  if (selected !== null) {
    return (
      <TeamPersonDetail
        // Keyed by person: the card's children hold useState DRAFTS seeded from props (name,
        // responsibilities, new-address). Without a remount per person, a rail switch leaves the
        // PREVIOUS person's draft on screen — and a blur commits it onto the NEW person.
        key={selected.id}
        user={selected}
        contacts={directory.contacts[selected.id]}
        canEditContacts={directory.canEditContacts}
        onProposeChange={routes.onProposeChange}
        onAddAddress={routes.onAddAddress}
        onRetireAddress={routes.onRetireAddress}
        onEditPerson={routes.onEditPerson}
      />
    );
  }
  return (
    <section aria-label="Person" className="tm-detail">
      <div className="tm-card tm-empty-card">
        <div className="empty-line">No people in the directory yet.</div>
      </div>
    </section>
  );
}

/** The loaded body — a TOP-LEVEL component (an inline one would remount per render and wipe child
 *  state, the deep-sweep N5 remount lesson): head row, outcome note, then the rail + right pane. */
function TeamBody({
  directory,
  sel,
  routes,
}: {
  readonly directory: DirectoryProjection;
  readonly sel: TeamSelection;
  /** The propose outcome lives in here — held ABOVE the remount boundary so a re-read cannot wipe it. */
  readonly routes: DirectoryRoutes;
}): React.JSX.Element {
  const selected =
    directory.users.find((user) => user.id === sel.selectedId) ?? directory.users[0] ?? null;
  const effectivePane: TeamPane = sel.pane === 'add' ? 'add' : 'person';
  return (
    <>
      <TeamHead pane={effectivePane} sel={sel} />
      {routes.proposeNote !== null ? (
        <p role="status" className={routes.proposeNote.tone === 'ok' ? 'tm-note' : 'tm-note warn'}>
          {routes.proposeNote.text}
        </p>
      ) : null}
      <div className="tm-cols" data-directory>
        <TeamPeopleRail
          users={directory.users}
          selectedId={selected?.id ?? null}
          paneSwapped={effectivePane !== 'person'}
          onSelect={sel.onSelectPerson}
        />
        <TeamRightPane
          directory={directory}
          effectivePane={effectivePane}
          selected={selected}
          sel={sel}
          routes={routes}
        />
      </div>
    </>
  );
}

/** The Team surface. */
export function TeamSurface(): React.JSX.Element {
  const api = useSurfaceApi();
  const state = useProjection<DirectoryProjection>(api.getDirectory);
  const routes = useDirectoryRoutes(state.reload);
  const sel = useTeamSelection(routes.clearProposeNote);

  const active = state.data?.users.filter((user) => user.status === 'active').length ?? 0;

  return (
    <>
      <ScreenToolbar title="Team">
        {state.data !== null ? (
          <TbStamp>
            {active} of {state.data.users.length} active
          </TbStamp>
        ) : null}
        <TbInfo
          title="Team"
          tip="The single source of truth for people. Every task owner, approver and recipient resolves to a person here. Anyone who can save team changes edits contact details here directly; everyone else suggests a change. Ritona can suggest too, never commit."
        />
      </ScreenToolbar>
      <ScreenBody>
        <SurfaceFrame state={state}>
          {(directory) => <TeamBody directory={directory} sel={sel} routes={routes} />}
        </SurfaceFrame>
      </ScreenBody>
    </>
  );
}
